import CustomInput from '@/components/common/CustomInput';
import { Button } from '@/components/ui/button';
import { useAddMemberMutation, useAddMemberRoleMutation } from '@/store/api/commonApi';
import {
  useGetAccessDetailsQuery,
  useGetRolesListingQuery,
  useUpdateAccessPermissionMutation,
} from '@/store/api/rolesApi';
import { Field } from '@/types';
import { showToastMessage } from '@/utils';
import { manageRoleSchema } from '@/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Copy, Wand2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

const generatePassword = () => {
  return 'asdf1234';
};
interface CreateMemberProps {
  onCancel?: () => void;
  id?: number;
  type?: string;
}
type MemberFormType = z.infer<ReturnType<typeof manageRoleSchema>>;

const CreateAccessControl: React.FC<CreateMemberProps> = ({ onCancel, id, type }) => {
  const isViewMode = type === 'view';
  const isEditMode = type === 'edit';

  const [updateAccessPermission, { isLoading: isUpdating }] = useUpdateAccessPermissionMutation();
  const [addMember, { isLoading }] = useAddMemberRoleMutation();
  const [passwordRevealTrigger, setPasswordRevealTrigger] = useState<number | undefined>(undefined);
  // Form setup
  const { control, handleSubmit, reset, watch, setValue, getValues } = useForm<MemberFormType>({
    resolver: zodResolver(manageRoleSchema(type)),
    defaultValues: {
      firstname: '',
      // lastname: '',
      // username: '',
      email: '',
      password: '',
      transactionCode: '',
      mobile: '',
      role: 0,
    },
    mode: 'onChange',
  });
  const formValues = watch();

  const { data } = useGetRolesListingQuery({});
  const { data: editData } = useGetAccessDetailsQuery(Number(id), {
    skip: !id,
  });
  useEffect(() => {
    if (editData) {
      reset({
        firstname: editData.firstname || '',
        // username: editData.username || '',
        // lastname: editData.lastname || '',
        email: editData.email || '',
        mobile: editData.mobile || '',
        password: '', // keep empty for edit
        role: editData?.role?.id,
        transactionCode: '',
      });
    }
  }, [editData, reset]);
  const transformedData = data?.map((item) => ({
    id: item?.id,
    label: item.name,
    value: item?.id,
  }));

  // Updated Fields (confirmPassword removed, transactionCode Field)
  const fields: Field[] = [
    { label: 'First Name', type: 'text', name: 'firstname', placeholder: 'Enter First name' },
    // { label: 'User Name', type: 'text', name: 'username', placeholder: 'Enter User name ' },
    // { label: 'Last Name', type: 'text', name: 'lastname', placeholder: 'Enter Last' },
    { label: 'Email', type: 'text', name: 'email', placeholder: 'Enter Email' },
    {
      label: 'Phone Number',
      type: 'phone',
      name: 'mobile',
      placeholder: 'Enter Phone Number',
    },

    {
      label: 'Role',
      type: 'select',
      name: 'role',
      placeholder: 'Select Role Type',
      options: transformedData,
    },
    ...(!isViewMode && !isEditMode
      ? [
          {
            label: 'Password',
            type: 'password',
            name: 'password',
            placeholder: 'Enter Password',
          },
          {
            label: 'Transaction Code',
            type: 'text',
            name: 'transactionCode',
            placeholder: 'Enter Transaction Code',
          },
        ]
      : []),
  ];

  const handleGeneratePassword = () => {
    const password = generatePassword();
    setValue('password', password, { shouldValidate: true, shouldDirty: true });
    setPasswordRevealTrigger((prev) => (prev ?? 0) + 1);
  };

  const handleCopyPassword = async () => {
    const password = getValues('password');
    if (!password) {
      showToastMessage('error', 'Nothing to copy. Generate or enter a password first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(password);
      showToastMessage('success', 'Password copied to clipboard');
    } catch {
      showToastMessage('error', 'Failed to copy password');
    }
  };

  const onSubmit = async (data: MemberFormType) => {
    try {
      if (isEditMode) {
        if (!id) return;
        await updateAccessPermission({
          id,
          firstname: data.firstname,
          // username: data.username,
          // lastname: data.lastname,
          email: data.email,
          ...(data?.mobile &&
            data.mobile.length > 4 && {
              mobile: data.mobile,
            }),
          roleId: Number(data.role),
          isRole: 1,
        }).unwrap();

        showToastMessage('success', 'Member Updated Successfully!');
      } else {
        await addMember({
          firstname: data.firstname,
          // username: data.username,
          email: data.email,
          ...(data?.mobile &&
            data.mobile.length > 4 && {
              mobile: data.mobile,
            }),
          password: data.password || '',
          transactionCode: data.transactionCode,
          roleId: Number(data.role),
          isRole: 1,
        }).unwrap();

        showToastMessage('success', 'Member Added Successfully!');
      }

      onCancel?.();
      reset();
    } catch (err: any) {
      const msg = err?.data?.message || 'Something went wrong';
      showToastMessage('error', msg);
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-1">
      {fields.map((field) => (
        <div key={field.name}>
          <CustomInput
            key={field.name}
            control={control}
            name={field.name}
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            inputClassName="pr-10 text-grayish-blue !border-gray !bg-green/10 disabled:text-black"
            labelClassName="text-[#162f34] font-medium"
            maxLength={field.name === 'mobile' ? 10 : field.name === 'transactionCode' ? 5 : 100}
            disabled={isViewMode}
            options={field?.options}
            revealTrigger={field.name === 'password' ? passwordRevealTrigger : undefined}
          />

          {/* {field.name === 'password' && (
            <div className="flex justify-end gap-3 mt-1.5">
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs text-white bg-rsplay-navy hover:bg-rsplay-navy/90 transition"
              >
                <Wand2 size={14} />
                Generate Password
              </button>
              <button
                type="button"
                onClick={handleCopyPassword}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs text-white bg-rsplay-navy hover:bg-rsplay-navy/90 transition"
              >
                <Copy size={14} />
                Copy
              </button>
            </div>
          )} */}
        </div>
      ))}

      {/* Buttons */}
      <div className="flex justify-end gap-4 mt-1 border-t border-gray/20 pt-2">
        <Button
          type="button"
          onClick={() => {
            onCancel?.();
            reset();
          }}
          variant="destructive"
        >
          Cancel
        </Button>

        <Button type="submit" disabled={isLoading || isUpdating}>
          {isLoading || isUpdating
            ? type === 'edit'
              ? 'Updating...'
              : 'Creating...'
            : type === 'edit'
              ? 'Update'
              : 'Create'}
        </Button>
      </div>
    </form>
  );
};

export default CreateAccessControl;
